import { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Alert, Flex, Link, Text, useToast } from '@vimeo/bokeh';
import {
  CommentsFooter,
  CommentsList,
  CommentStatus,
  Sort,
  FilterOptions,
  SortComments,
  ResolvedCommentFilter,
  CommentFilters,
  CommentType,
} from '@vimeo/comments-ui';
import useSessionStorage from '@vimeo/core/hooks/use-storage/useClientStorage';
import { translate } from '@vimeo/i18n-runtime';

import { CommentsContext, PrivateComment } from '../AnnotationsOverlay/CommentsContext';
import { useGetCommentsCount } from '../hooks';
import { usePostComments } from '../hooks/comments/use-add-comments';
import { usePostReply } from '../hooks/comments/use-add-replies';
import { useDeleteComments } from '../hooks/comments/use-delete-comments';
import { useDeleteReply } from '../hooks/comments/use-delete-reply';
import { useEditComment } from '../hooks/comments/use-edit-comments';
import { useEditReply } from '../hooks/comments/use-edit-reply';
import { useEnrichCommentsWithTracking } from '../hooks/comments/use-enrich-comments-with-tracking';
import { useFilteredComments } from '../hooks/comments/use-filter-comments';
import { useGetComments } from '../hooks/comments/use-get-comments';
import { useGetCommentsDeeplink } from '../hooks/comments/use-get-deeplink-page';
import { useVideoPrivacy } from '../hooks/comments/use-video-privacy';
import { useEditVideoData } from '../hooks/use-edit-video-data';
import { withGctlProvider } from '../hooks/use-gctl-provider';
import { useGetBpSessionId } from '../hooks/use-get-bp-session-id';
import { guestLoginModalContext, withGuestLoginModal } from '../hooks/use-guestlogin-modal';
import { useInfiniteScroll } from '../hooks/use-infinite-scroll';
import { useSortPreference } from '../hooks/use-sort-preference';
import { useUserPermissions } from '../hooks/use-user-permission';
import { useVideoData } from '../hooks/use-video-data';
import { useViewer } from '../hooks/use-viewer';
import {
  bpOpenCommentOptions,
  bpCopyCommentLink,
  bpProfileLink,
  bpSortComment,
  bpToggleShowResolvedComments,
  bpFilterComments,
} from '../utils/analytics';
import { STASHED_COMMENTS_KEY } from '../utils/constants';
import {
  idFromUri,
  FilterToBPOptionMap,
  extractDeepLinkReplyId,
  checkForMention,
  sanitiseSpaceInRichtext,
} from '../utils/helper';
import { getStorageInstance } from '../utils/inMemoryStorage';
import { CommentMentionModule } from './CommentMentionModule';
import { ShowCommentsConfirmationModal } from './ShowCommentsConfirmationModal';
import { AnalyticsPropsType } from './types';

export type CommentsContainerPropsType = {
  clipRequestId: string;
  clipId: string;
  isPublic: boolean;
  searchQuery?: string;
  onMomentPlay: (timecode: number) => void;
  analyticsProps?: AnalyticsPropsType;
  isUserProfileClickable?: boolean;
  saveViewerCommentsSetting?: (allowViewerComments: boolean) => void;
  setSearchCommentCount?: (count?: number) => void;
  showCommentsHiddenAlert?: boolean;
  videoVersionUri?: string;
  targetApiVersion?: string;
  isPreviousVersion?: boolean;
  onReportClick?: (commentId?: string, isReply?: boolean, commentOwnerId?: string) => void;
  showcaseId?: number;
  reviewId?: string;
  showResolvedComments?: boolean;
};

export const CommentsContainer = withGuestLoginModal(
  withGctlProvider(
    ({
      clipRequestId,
      clipId,
      isPublic,
      searchQuery = '',
      onMomentPlay,
      analyticsProps,
      isUserProfileClickable = true,
      saveViewerCommentsSetting,
      setSearchCommentCount,
      showCommentsHiddenAlert = false,
      videoVersionUri,
      targetApiVersion,
      isPreviousVersion = false,
      onReportClick,
      showcaseId,
      reviewId,
      showResolvedComments = true,
    }: CommentsContainerPropsType) => {
      const toast = useToast();
      const viewer = useViewer();
      const sessionId = useGetBpSessionId();

      const { videoData, videoDataLoading } = useVideoData(clipRequestId, showcaseId, reviewId);
      const [sort, updateSort] = useSortPreference(
        { value: 'newest', label: translate('Newest'), type: 'date', direction: 'desc' },
        'comments-container-sort',
      );
      const initalFilterOption: FilterOptions = {
        status: 'open',
        userType: 'everyone',
        mentionAndReplies: false,
        videoVersionUri,
        targetApiVersion,
      };
      const [filterOptions, setFilterOptions] = useState<FilterOptions>(initalFilterOption);
      const [showOnlyUnread, setShowOnlyUnread] = useState(false);

      const { initialPage, initialPageLoading, deeplinkNoteId, deeplinkDataError } =
        useGetCommentsDeeplink(clipRequestId, isPublic, sort, showcaseId, reviewId);

      const deepLinkReplyId = extractDeepLinkReplyId();

      const {
        comments,
        commentsLoading,
        loadMoreComments,
        isLoadingMoreComments,
        isCommentsDone,
        page: apiPage,
        copyComment,
        commentsError,
      } = useGetComments(clipRequestId, isPublic, sort, filterOptions, showcaseId, reviewId);

      const { changeCommentsCount } = useGetCommentsCount(
        clipRequestId,
        isPublic,
        {
          videoVersionUri,
          targetApiVersion,
        },
        showcaseId,
        reviewId,
      );

      const { privacy } = useVideoPrivacy(clipRequestId, showcaseId, reviewId);
      const {
        guestUser,
        modalState: { isActive },
      } = useContext(guestLoginModalContext);

      const skipFilter = Boolean(
        !searchQuery || commentsLoading || (comments && comments.length === 0),
      );
      const { filteredComments, matchCount } = useFilteredComments(
        skipFilter,
        comments,
        searchQuery,
      );

      const commentsToDisplay = useEnrichCommentsWithTracking(filteredComments, showOnlyUnread);

      const canAddComments = !isPublic || (isPublic && privacy?.comments !== 'nobody');
      const canResolveComment = !isPublic && showResolvedComments;

      const canLoadMore =
        !isCommentsDone && !commentsLoading && !isLoadingMoreComments && !searchQuery; // Controls when to load more comments in useInfiniteScroll
      const [page, lastElementRef, setPage] = useInfiniteScroll(canLoadMore, apiPage);

      const [activeCommentId, setActiveCommentId] = useState(deepLinkReplyId || deeplinkNoteId);

      const storage = getStorageInstance();

      const {
        value: stashedComment,
        set: setStashedComment,
        remove: clearStashedComment,
      } = useSessionStorage(STASHED_COMMENTS_KEY, null, undefined, storage);

      const onDeleteFailure = () => {
        toast({ status: 'error', title: translate('Unable to delete comment') });
      };

      useEffect(() => {
        if (apiPage > page) {
          setPage(apiPage);
        }
        if (page > apiPage) {
          loadMoreComments(page); // Loads next page of comments when page from infinite scroll increments
        }
      }, [apiPage, loadMoreComments, page, setPage]);

      useEffect(() => {
        if (initialPage && initialPage > 0) {
          setPage(initialPage as number); // Sets the initial page of infinite scroll hook to the page of the deeplinked comment
        }
      }, [initialPage, setPage]);

      const { setComments, setActiveComment } = useContext(CommentsContext);

      const handleMomentPlay = useCallback(
        (timecode: number, comment?: CommentType) => {
          if (comment) {
            setActiveComment(comment as PrivateComment);
          }
          onMomentPlay(timecode);
        },
        [onMomentPlay, setActiveComment],
      );

      useEffect(() => {
        if (!isPublic && comments) {
          //@ts-expect-error type mismatch as comments represent both private and public comments
          setComments(comments);
        }
      }, [comments, isPublic, setComments]);

      useEffect(() => {
        if (setSearchCommentCount) {
          setSearchCommentCount(matchCount);
        }
      }, [setSearchCommentCount, matchCount]);

      const { postComment, postCommentLoading, postCommentError } = usePostComments(
        clipRequestId,
        isPublic,
        sort,
        analyticsProps,
        filterOptions,
        showcaseId,
        reviewId,
      );

      const { deleteComment: deleteCommentApi, deleteCommentLoading } = useDeleteComments(
        clipRequestId,
        onDeleteFailure,
        isPublic,
        sort,
        analyticsProps,
        filterOptions,
        showcaseId,
        reviewId,
      );

      const { deleteReply: deleteReplyApi, deleteReplyLoading } = useDeleteReply(
        clipRequestId,
        onDeleteFailure,
        isPublic,
        sort,
        analyticsProps,
        filterOptions,
        showcaseId,
        reviewId,
      );

      const {
        resolveComment,
        unresolveComment,
        updateCommentText,
        editCommentLoading,
        editCommentError,
      } = useEditComment(
        clipRequestId,
        isPublic,
        sort,
        analyticsProps,
        filterOptions,
        showcaseId,
        reviewId,
      );

      const toggleCommentStatus = (commentId: string, status: CommentStatus) => {
        status === 'closed' ? unresolveComment(commentId) : resolveComment(commentId);
      };

      const { patchReply, patchReplyLoading, patchReplyError } = useEditReply(
        clipRequestId,
        isPublic,
        sort,
        analyticsProps,
        filterOptions,
        showcaseId,
        reviewId,
      );

      const { postReply, postReplyLoading, postReplyError } = usePostReply(
        clipRequestId,
        isPublic,
        sort,
        analyticsProps,
        filterOptions,
        showcaseId,
        reviewId,
      );

      const addComment = (text: string) => {
        let commentText = sanitiseSpaceInRichtext(text);
        if (!viewer?.user && !guestUser) {
          const stringifiedComment = JSON.stringify({ text: commentText, isReply: false });
          setStashedComment(stringifiedComment);
        }
        postComment(commentText).then(() => changeCommentsCount?.increaseCommentsCount());
        setActiveCommentId(null);
      };

      const addReply = (commentId: string, text: string, name?: string) => {
        let replyText = sanitiseSpaceInRichtext(text);
        if (!viewer?.user && !guestUser) {
          const stringifiedComment = JSON.stringify({
            text: replyText,
            isReply: true,
            commentId,
            name,
          });
          setStashedComment(stringifiedComment);
        }
        postReply(replyText, commentId, name).then(() =>
          changeCommentsCount?.increaseCommentsCount(),
        );
        setActiveCommentId(null);
      };

      const editComment = (commentId: string, text: string) => {
        let editedCommentText = sanitiseSpaceInRichtext(text);
        updateCommentText(commentId, editedCommentText);
        setActiveCommentId(null);
      };

      const editReply = (replyId: string, text: string) => {
        let editedReplyText = sanitiseSpaceInRichtext(text);
        patchReply(replyId, editedReplyText);
        setActiveCommentId(null);
      };

      const deleteReply = (commentId: string, isTimeCoded?: boolean) => {
        deleteReplyApi(commentId, isTimeCoded);
        setActiveCommentId(null);
      };

      const deleteComment = (commentId: string, isTimeCoded?: boolean) => {
        deleteCommentApi(commentId, isTimeCoded);
        setActiveCommentId(null);
      };

      const richCommentComponent = useCallback(
        (
          textAreaProps: Omit<React.ComponentProps<typeof CommentMentionModule>, 'clipRequestId'>,
        ) => {
          return (
            <CommentMentionModule
              clipRequestId={clipRequestId}
              {...textAreaProps}
              analyticsProps={analyticsProps}
              showcaseId={showcaseId}
              reviewId={reviewId}
            />
          );
        },
        //videoDataLoading is added as a deps to force the component to re-render when videoData changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clipRequestId, analyticsProps, videoDataLoading, guestUser],
      );

      const onOpenCommentOptionsClick = (
        commentId: string,
        isReply: boolean,
        commentOwnerId?: string,
        isTimeCoded?: boolean,
      ) => {
        if (analyticsProps) {
          analyticsProps = {
            ...analyticsProps,
            commentContextFields: {
              comment_id: idFromUri(commentId),
              is_reply: isReply,
              comment_owner_id: idFromUri(commentOwnerId),
            },
          };
          bpOpenCommentOptions(
            clipId,
            privacy?.view,
            videoData?.uploader.link,
            isPublic,
            analyticsProps,
            viewer,
            isTimeCoded,
          );
        }
      };

      const onCopyClick = (
        commentId: string,
        commentOwnerId?: string,
        isTimeCoded?: boolean,
        parentCommentId?: string,
        text?: string,
      ) => {
        copyComment(commentId, parentCommentId);
        let isMention = checkForMention(text);
        if (analyticsProps) {
          analyticsProps = {
            ...analyticsProps,
            commentContextFields: {
              comment_id: idFromUri(commentId),
              is_reply: !!parentCommentId,
              comment_owner_id: idFromUri(commentOwnerId),
              is_mention: isMention,
            },
          };
          bpCopyCommentLink(
            clipId,
            privacy?.view,
            videoData?.uploader.link,
            isPublic,
            analyticsProps,
            viewer,
            isTimeCoded,
          ); // fire event when copy comment link button is clicked
        }
      };

      const onUserProfileClick = (commentId: string, isReply: boolean, commentOwnerId?: string) => {
        if (analyticsProps) {
          analyticsProps = {
            ...analyticsProps,
            commentContextFields: {
              comment_id: idFromUri(commentId),
              is_reply: isReply,
              comment_owner_id: idFromUri(commentOwnerId),
            },
          };
          bpProfileLink(clipId, sessionId, analyticsProps, viewer);
        }
      };

      const commentsFetchingError = commentsError || deeplinkDataError;
      const { canEdit } = useUserPermissions(clipRequestId, showcaseId, reviewId);

      const setSort = (sortOption: Sort) => {
        updateSort(sortOption);
      };

      const onSortSelect = (sortOption: Sort) => {
        if (analyticsProps) {
          bpSortComment(
            sortOption.value,
            clipId,
            privacy?.view,
            videoData?.uploader.link,
            isPublic,
            analyticsProps,
            viewer,
          );
        }
      };

      const updateFilterOptions = (newFilterOptions: FilterOptions) => {
        setPage(1);
        setFilterOptions({ ...filterOptions, ...newFilterOptions });
      };

      const clearFilters = () => {
        updateFilterOptions(initalFilterOption);
        setShowOnlyUnread(false);
      };

      useEffect(
        () => {
          const savedComment = JSON.parse(stashedComment);
          const curUser = viewer?.user || guestUser;
          if (
            curUser &&
            savedComment?.text &&
            !videoDataLoading &&
            !postCommentLoading &&
            !postReplyLoading
          ) {
            clearStashedComment();
            const res = savedComment.isReply
              ? postReply(savedComment.text, savedComment.commentId)
              : postComment(savedComment.text);
            res.then(() => changeCommentsCount?.increaseCommentsCount());
          }
        },
        //run this only when below deps change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [viewer?.user, isPublic, videoDataLoading, postCommentLoading, postReplyLoading, guestUser],
      );

      const [isShowCommentModalActive, setIsShowCommentModalActive] = useState(false);
      const { saveViewerPageCommentPreference, loading: isEditApiInProgress } = useEditVideoData(
        clipRequestId,
        showcaseId,
        reviewId,
      );
      const toastRef = useRef<string | number>('');

      const showCommentsOnVHPage = () => {
        saveViewerPageCommentPreference(true)
          .then(() => {
            setIsShowCommentModalActive(false);
            toastRef.current = toast({
              title: translate({
                singular: 'Comments turned on in Appearance settings. {LINK}Undo{/LINK}',
                replacements: {
                  LINK: (children: ReactNode) => (
                    <Link
                      color="inherit"
                      _hover={{ cursor: 'pointer', _dark: { color: 'inherit' } }}
                      onClick={() => {
                        toast.close(toastRef.current);
                        saveViewerPageCommentPreference(false);
                      }}
                      fontSize={'body-md'}
                      variant="inline-primary"
                    >
                      {children}
                    </Link>
                  ),
                },
              }),
              duration: 3000,
            });
          })
          .catch(() => {
            toast({
              title: translate({
                singular: 'Unable to turn on comments. {LINK}Try again{/LINK}',
                replacements: {
                  LINK: (children: ReactNode) => (
                    <Link
                      color="inherit"
                      onClick={() => setIsShowCommentModalActive(true)}
                      _hover={{ cursor: 'pointer', _dark: { color: 'inherit' } }}
                      fontSize={'body-md'}
                      variant="inline-primary"
                    >
                      {children}
                    </Link>
                  ),
                },
              }),
              duration: 3000,
            });
          });
      };

      const areCommentsHiddenOnVhPage = videoData && videoData.page && !videoData.page.comments;
      const hasOnlyCommentAccess =
        !videoData?.metadata?.interactions?.edit?.uri &&
        videoData?.metadata?.interactions?.canComment;

      return (
        <>
          <Flex
            justifyContent={!canResolveComment ? 'flex-end' : 'space-between'}
            px={{ base: 200, md: 300 }}
            py="8px"
          >
            {canResolveComment && (
              <ResolvedCommentFilter
                showResolved={filterOptions?.status !== 'open'}
                updateFilterOptions={(newFilterOption) => {
                  updateFilterOptions(newFilterOption);
                  if (analyticsProps) {
                    bpToggleShowResolvedComments(
                      !Boolean(newFilterOption.status),
                      clipId,
                      privacy?.view,
                      analyticsProps,
                      viewer,
                    );
                  }
                }}
              />
            )}
            <Flex>
              <SortComments
                defaultSort={sort.label}
                isPublic={isPublic}
                setSort={setSort}
                onSortSelect={onSortSelect}
              />
              <CommentFilters
                filterOptions={filterOptions}
                updateFilterOptions={(newFilterOption) => {
                  if (analyticsProps) {
                    bpFilterComments(
                      FilterToBPOptionMap[newFilterOption.userType ?? 'mentions_replies'].option,
                      FilterToBPOptionMap[newFilterOption.userType ?? 'mentions_replies'].copy,
                      clipId,
                      isPublic,
                      privacy?.view,
                      analyticsProps,
                      viewer,
                    );
                  }
                  updateFilterOptions(newFilterOption);
                }}
                onUnreadFilterToggle={(isUnreadActive) => setShowOnlyUnread(isUnreadActive)}
                showOnlyUnread={showOnlyUnread}
                isLoggedOut={!viewer?.user}
              />
            </Flex>
          </Flex>
          {isPreviousVersion && (
            <Flex px={{ base: 200, md: 300 }}>
              <Alert display="flex" alignItems="center" status="info" size="sm">
                <Text variant="body-xl" fontSize="body-sm" letterSpacing="0.24px">
                  {translate({
                    singular: 'To add a comment, {LINK}view the current version{/LINK}',
                    replacements: {
                      LINK: (children: string) => (
                        <Link
                          fontSize="body-sm"
                          variant="inline-primary"
                          href={
                            reviewId
                              ? `/reviews/${reviewId}/videos/${clipRequestId}`
                              : `/manage/videos/${clipRequestId}`
                          }
                          _hover={{ cursor: 'pointer' }}
                        >
                          {children}
                        </Link>
                      ),
                    },
                  })}
                </Text>
              </Alert>
            </Flex>
          )}
          {isPublic && areCommentsHiddenOnVhPage && showCommentsHiddenAlert && !isPreviousVersion && (
            <Flex px={{ base: 200, md: 300 }}>
              <Alert status="info">
                <Text variant="body-xl" display="inline" fontSize="body-sm">
                  {translate('Viewer comments are currently hidden on the video page.')}
                  <Link
                    fontSize="body-sm"
                    variant="inline-primary"
                    onClick={() => setIsShowCommentModalActive(true)}
                    pl="4px"
                    _hover={{ cursor: 'pointer' }}
                  >
                    {translate('Show comments')}
                  </Link>
                </Text>
              </Alert>
            </Flex>
          )}
          <CommentsList
            comments={commentsToDisplay}
            toggleCommentStatus={toggleCommentStatus}
            isPublic={isPublic}
            addReply={addReply}
            deleteComment={deleteComment}
            deleteReply={deleteReply}
            editComment={editComment}
            editReply={editReply}
            lastElementRef={lastElementRef}
            canAddComments={canAddComments}
            deeplinkedCommentId={activeCommentId?.toString()}
            isCommentsLoading={commentsLoading || initialPageLoading}
            isSubmitTextLoading={postReplyLoading || editCommentLoading || patchReplyLoading}
            isDeleteLoading={deleteCommentLoading || deleteReplyLoading}
            isMoreCommentsLoading={isLoadingMoreComments || false}
            richCommentComponent={richCommentComponent}
            onCopyClick={onCopyClick}
            onOpenCommentOptionsClick={onOpenCommentOptionsClick}
            isUserProfileClickable={isUserProfileClickable}
            onUserProfileClick={onUserProfileClick}
            commentsFetchingError={commentsFetchingError}
            editCommentError={editCommentError}
            editReplyError={patchReplyError}
            replyError={postReplyError}
            onMomentPlay={handleMomentPlay}
            canResolveComment={canEdit && showResolvedComments}
            searchQuery={searchQuery}
            appliedFilter={
              filterOptions?.userType !== 'everyone' ||
              filterOptions?.mentionAndReplies === true ||
              showOnlyUnread === true
            }
            clearFilters={clearFilters}
            saveViewerCommentsSetting={saveViewerCommentsSetting}
            isGuestModalActive={isActive}
            hasOnlyCommentAccess={hasOnlyCommentAccess}
            isPreviousVersion={isPreviousVersion}
            onReportClick={onReportClick}
            videoPrivacy={privacy?.view}
            reviewId={reviewId}
          />
          <CommentsFooter
            richCommentComponent={richCommentComponent}
            isLoading={postCommentLoading}
            isPublic={isPublic}
            onSubmit={addComment}
            canAddComments={canAddComments}
            hasComments={!!comments?.length}
            errorMessage={postCommentError?.errorMessage}
            saveViewerCommentsSetting={saveViewerCommentsSetting}
            isGuestModalActive={isActive}
            hasOnlyCommentAccess={hasOnlyCommentAccess}
            isPreviousVersion={isPreviousVersion}
          />
          <ShowCommentsConfirmationModal
            isActive={isShowCommentModalActive}
            setActive={setIsShowCommentModalActive}
            isLoading={isEditApiInProgress}
            onShowClick={showCommentsOnVHPage}
          />
        </>
      );
    },
  ),
);
